/**
 * An order's state as the account words it — one status per order, the same on the overview and
 * in the orders list. `DeliveryRejected` is the couriers plugin's state for a parcel nobody
 * collected; `PaymentAuthorized` is where a bank transfer waits until the money is matched.
 */
export type AccountOrderStatus
  = | 'awaiting_payment'
    | 'processing'
    | 'on_the_way'
    | 'delivered'
    | 'not_collected'
    | 'cancelled'
    | 'returned'

export function accountOrderStatus(state: string): AccountOrderStatus {
  switch (state) {
    case 'PaymentAuthorized':
    case 'ArrangingAdditionalPayment':
      return 'awaiting_payment'
    case 'PartiallyShipped':
    case 'Shipped':
      return 'on_the_way'
    case 'PartiallyDelivered':
    case 'Delivered':
      return 'delivered'
    case 'DeliveryRejected':
      return 'not_collected'
    case 'Cancelled':
      return 'cancelled'
    case 'Returned':
      return 'returned'
    default:
      return 'processing'
  }
}

/**
 * The status's colour, by how far the order has got: amber while it is with us (awaiting the
 * transfer, or being packed), blue on its way, green delivered, red when nobody collected it, grey
 * once it ended without the customer keeping it. Classes for text, fills and outlines; the
 * colours are the account layout's `--account-*` tokens.
 */
export function accountOrderStatusTone(status: AccountOrderStatus): { text: string, bg: string, border: string } {
  switch (status) {
    case 'awaiting_payment':
    case 'processing':
      return { text: 'text-[var(--account-waiting)]', bg: 'bg-[var(--account-waiting)]', border: 'border-[var(--account-waiting)]' }
    case 'on_the_way':
      return { text: 'text-[var(--account-transit)]', bg: 'bg-[var(--account-transit)]', border: 'border-[var(--account-transit)]' }
    case 'delivered':
      return { text: 'text-[var(--account-positive)]', bg: 'bg-[var(--account-positive)]', border: 'border-[var(--account-positive)]' }
    case 'not_collected':
      return { text: 'text-[var(--account-alert)]', bg: 'bg-[var(--account-alert)]', border: 'border-[var(--account-alert)]' }
    default:
      return { text: 'text-[var(--account-ended)]', bg: 'bg-[var(--account-ended)]', border: 'border-[var(--account-ended)]' }
  }
}

/**
 * The step icons of the order's way to the customer (`OrderProgress`), which the status label
 * repeats beside its word: the parcel while it is with us, the truck, the tick once it arrived.
 */
export const ACCOUNT_ORDER_STEP_ICONS = ['ph:package-light', 'ph:truck-light', 'ph:check-light'] as const

/** The status's icon: its step's, or for an order that left the way, what happened to it. */
export function accountOrderStatusIcon(status: AccountOrderStatus): string {
  switch (status) {
    case 'awaiting_payment':
    case 'processing':
      return ACCOUNT_ORDER_STEP_ICONS[0]
    case 'on_the_way':
      return ACCOUNT_ORDER_STEP_ICONS[1]
    case 'delivered':
      return ACCOUNT_ORDER_STEP_ICONS[2]
    case 'not_collected':
      return 'ph:warning-circle-light'
    case 'returned':
      return 'ph:arrow-u-up-left-light'
    default:
      return 'ph:x-circle-light'
  }
}

/**
 * An Exalted Priority & Care order, marked with `OrderPriorityBadge`. The mark is never cleared (an
 * unpaid bank transfer is marked when placed and cancelled later), but a cancelled order is not
 * dispatched, so it is not shown as a priority one.
 */
export function isAccountPriorityOrder(order: { state: string, customFields?: { isPriority?: boolean | null } | null }): boolean {
  return order.customFields?.isPriority === true && accountOrderStatus(order.state) !== 'cancelled'
}
